import React, { ReactNode, useContext, useState } from 'react';
import { keyframes, styled } from 'styled-components';
import { LFC } from '../../../Legacy';
import { Colors } from '../../../ui';
import { useComponentObserver } from '../../canvas/visualization/ComponentObserver';
import { PressableProps, RenderBounds, RenderBox, RenderInner, useDragInteractable } from '../../canvas/visualization/RenderHelpers';
import { RenderingContext } from '../../canvas/visualization/RenderingContext';
import { Tray } from './Tray';
import { TrayLayout } from './TrayLayout';

const RenderBorder = React.memo<{ layout: TrayLayout }>(({ layout }) => {
    const { base } = layout;
    return <>
        <RenderBox
            {...base.toCSS()}
            background={Colors.white}
            borderRadius="6px"
            border={`solid 1px ${Colors.border}`}
            borderBottom={`solid 3px ${Colors.border}`}
        />
    </>;
});

const RenderEmpty = React.memo<{ layout: TrayLayout }>(({ layout }) => {
    const { base } = layout;
    const style = {
        ...base.toCSS(),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '10px',
        color: Colors.textLight,
        fontSize: '16px',
        fontFamily: 'Rubik',
        textAlign: 'center',
    };
    return <RenderBox {...style}>
        Choose a preset or<br /> drop an item in here.
    </RenderBox>;
});

type RenderItemButtonProps = PressableProps & { selected?: boolean, color?: string, children?: ReactNode };
const RenderItemButton = React.memo<RenderItemButtonProps>(({ area, state, selected, color, children }) => {

    const background =
        (state === 'hover' ? Colors.hover : null) ??
        (state === 'active' ? Colors.active : null) ??
        null;

    const props = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    };

    return <RenderBox {...area.toCSS()} {...props} color={color}>
        {children}
        <RenderBox inset="0" borderRadius="4px" {...{ background }} />
    </RenderBox>;
});


const jiggle = keyframes`
    0% {
        transform: rotate(-3deg);
    }
    50% {
        transform: rotate(3deg);
    }
    100% {
        transform: rotate(-3deg);
    }
`;

const RenderItemInner = styled.div<{ jiggle: boolean, delay: number }>`
    position: absolute;
    left: 50%;
    top: 50%;
    width: 0;
    height: 0;
    z-index: 1;
    & > * {
        transform: translate(-50%, -50%) !important;
    }
        
    animation: ${p => p.jiggle ? jiggle : null} .35s linear infinite;
    animation-delay: -${p => p.delay}s;
`;

const RenderItem = ({ object, index, children }: { object: Tray, index: number, children?: ReactNode }) => {

    const [delay] = useState(Math.random());
    const item = object.children[index];
    useComponentObserver(item);

    const color = Colors.text;
    const active = useDragInteractable(object, `item:${index}`);

    return <RenderItemButton {...{ color, ...active }}>
        <RenderItemInner jiggle={object.editing} delay={delay}>
            {children}
        </RenderItemInner>
    </RenderItemButton>;
};

export const TrayVisual: LFC<{ object: Tray }> = ({ object, children }) => {

    const { gfxLevel } = useContext(RenderingContext);
    const childNodes = React.Children.toArray(children);
    const { layout } = object;

    return <>
        <RenderBounds object={object}>
            <RenderInner object={object}>
                <RenderBorder layout={layout} />
                <RenderingContext.Provider value={{ type: 'isolated', gfxLevel }}>
                    {object.children.map((x, i) =>
                        <RenderItem key={x.id} object={object} index={i}>
                            {childNodes[i] || null}
                        </RenderItem>
                    )}
                    {!object.children.length &&
                        <RenderEmpty layout={layout} />
                    }
                </RenderingContext.Provider>
            </RenderInner>
        </RenderBounds>

    </>;
};
